import * as React from 'react'
import {observer} from 'mobx-react-lite'
import {Tab, TabProps as MuiTabProps, Tabs as MuiTabs, TabsProps as MuiTabsProps, Tooltip} from '@mui/material'
import {DataCyProps} from './dataCyProps'
import {syskronPalette} from '@oc/styles'

export interface TabInfo {
    header: {
        'data-cy'?: string
        label: string
        error?: boolean
        additionalProps?: MuiTabProps & DataCyProps & {tooltip?: string}
    }
    content: React.ReactNode
}

export interface TabsProps {
    tabs: TabInfo[]
    selectedTab: number
    setSelectedTab: (newValue: number) => void
    rootClassName?: string
    tabRootClassName?: string
    tabsProps?: Omit<MuiTabsProps, 'value' | 'onChange'>
    fallbackTabContent?: React.ReactNode
}

function _Tabs(props: TabsProps) {
    const {tabs, selectedTab, setSelectedTab, rootClassName, tabRootClassName, tabsProps, fallbackTabContent} = props

    const handleChange = React.useCallback(
        (_: React.ChangeEvent<unknown>, newValue: number) => {
            setSelectedTab(newValue)
        },
        [setSelectedTab]
    )

    const dataArea = React.useMemo(() => {
        const selected = tabs.at(selectedTab)
        return (
            <div role="tabpanel" className={tabRootClassName} key={`${selected?.header.label || 'unknown'}-content`}>
                {selected?.content || fallbackTabContent}
            </div>
        )
    }, [tabs, selectedTab, tabRootClassName, fallbackTabContent])

    const selectedTabMemo = React.useMemo(() => {
        if (selectedTab >= 0 && selectedTab < tabs.length) {
            return selectedTab
        }
        return false
    }, [selectedTab, tabs])

    return (
        <div className={rootClassName}>
            <MuiTabs textColor="primary" {...tabsProps} value={selectedTabMemo} onChange={handleChange}>
                {tabs.map(tabInfo => {
                    const tabElement = (
                        <Tab
                            key={`${tabInfo.header.label}-header`}
                            data-cy={tabInfo.header['data-cy']}
                            label={tabInfo.header.label}
                            style={
                                tabInfo.header.error
                                    ? {
                                          color: syskronPalette.common.red
                                      }
                                    : undefined
                            }
                            {...tabInfo.header.additionalProps}
                        />
                    )

                    return tabInfo.header.additionalProps?.disabled ? (
                        // Need this trick to have a tooltip on disabled element
                        <Tooltip title={tabInfo.header.additionalProps?.tooltip || ''}>
                            <span>{tabElement}</span>
                        </Tooltip>
                    ) : (
                        tabElement
                    )
                })}
            </MuiTabs>
            {dataArea}
        </div>
    )
}
export const Tabs = observer(_Tabs)

export default Tabs
